import { Link } from "react-router-dom";
import { ArrowRight, Users, FileText, Newspaper, Network, Phone, Mail } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

const SERVIZI = [
  {
    icon: Users,
    titolo: "Consulenza HR",
    intro: "Gestiamo attività di analisi, interpretazione e applicazione della normativa giuslavoristica e previdenziale. Offriamo supporto per la gestione del rapporto di lavoro con i dipendenti e ricerca di soluzioni innovative per rafforzare la talent retention in azienda.",
    bullets: [
      "Interpretazione autentica e applicazione dei CCNL sottoscritti",
      "Assunzioni agevolate",
      "Certificazione di best practice aziendali: parità di genere, società benefit, sostenibilità e volontariato aziendale",
    ],
  },
  {
    icon: FileText,
    titolo: "Assistenza e rappresentanza",
    intro: "Offriamo analisi dei modelli organizzativi, sottoscrizione di accordi territoriali integrativi al CCNL, definizione di contratti integrativi aziendali; rappresentiamo la controparte datoriale nell'ambito di Commissioni di Conciliazione, Certificazione e Arbitrato.",
    bullets: [
      "Accordi territoriali (detassazione premi, stagionalità, flessibilità organizzative)",
      "Contratti integrativi e regolamenti di Policy interna",
      "Procedure per il ricorso agli ammortizzatori sociali, licenziamenti individuali, plurimi e collettivi",
    ],
  },
  {
    icon: Newspaper,
    titolo: "Informazione",
    intro: "Offriamo un servizio di informazione in ambito HR, giuslavoristico e di prassi amministrativa, tramite newsletter settimanale.",
    bullets: [
      "Aggiornamenti su normative europee, nazionali e locali",
      "Indicazioni operative da INPS, INAIL e Ministero del Lavoro",
      "Monitoraggio delle pronunce della Corte di Cassazione e dei tribunali",
    ],
  },
  {
    icon: Network,
    titolo: "Networking",
    intro: "Favoriamo momenti di incontro e confronto tra gli HR delle aziende per lo scambio di know-how e best practice.",
    bullets: [
      "Eventi e webinar tematici",
      "Tavoli settoriali per registrare interessi e opportunità",
      "Collaborazioni con commercialisti e consulenti del lavoro",
    ],
  },
];

const TELEFONI = [
  "06.68437.221",
  "06.68437.333",
  "06.68437.238",
  "06.68437.290",
  "06.68437.334",
];

export default function ConsulenzaLavoroWelfarePage() {
  return (
    <main id="main-content" data-testid="consulenza-lavoro-welfare-page">
      {/* Hero */}
      <section className="bg-[var(--confcom-blue)] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <HeroBreadcrumb />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Consulenza sul lavoro e welfare
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-5">
            Un'esperienza di valore per la gestione delle risorse umane.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            Supportiamo le imprese in tutte le problematiche inerenti la <strong className="text-[var(--confcom-text-primary)]">gestione del personale</strong>, dal momento dell'instaurazione del rapporto di lavoro a quello della sua risoluzione. Siamo a fianco delle aziende nella <strong className="text-[var(--confcom-text-primary)]">risoluzione stragiudiziale</strong> delle controversie di lavoro.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
            Siamo presenti nelle <strong className="text-[var(--confcom-text-primary)]">Commissioni</strong> costituite presso gli Uffici Territoriali del Ministero del Lavoro, le Commissioni regionali e provinciali dell'INPS e presso gli Enti Bilaterali. Offriamo alle imprese informazione tempestiva e puntuale circa le novità normative attraverso <strong className="text-[var(--confcom-text-primary)]">circolari, incontri periodici e riunioni tecniche</strong> su argomenti specifici.
          </p>
        </div>
      </section>

      {/* Servizi dedicati */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-3 block">I servizi dedicati ai nostri associati</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] tracking-tight">
                Quattro aree di intervento
              </h2>
            </div>
            <Link
              to="/area-lavoro-welfare"
              className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm whitespace-nowrap"
              data-testid="link-area-lavoro-welfare"
            >
              Vai alla sezione Lavoro e Welfare <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {SERVIZI.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.titolo}
                  className="bg-white p-7 rounded-xl border border-[var(--confcom-border)]"
                  data-testid={`servizio-${item.titolo.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-11 h-11 bg-[var(--confcom-bg-blue-light)] rounded-xl flex items-center justify-center shrink-0">
                      <Icon size={22} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] pt-1.5">
                      {item.titolo}
                    </h3>
                  </div>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                    {item.intro}
                  </p>
                  <ul className="space-y-2">
                    {item.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-[var(--confcom-text-secondary)] leading-relaxed">
                        <span className="text-[var(--confcom-blue)] font-bold mt-0.5">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.95)" }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-4">Per maggiori informazioni</h2>
          <p className="text-white/80 mb-8">
            Contatta il nostro ufficio per qualsiasi esigenza relativa alla gestione del personale, alla normativa giuslavoristica e ai servizi di welfare.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href="mailto:sindacale@confcommercioroma.it"
              className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold hover:opacity-90 transition-opacity"
              data-testid="cta-email-sindacale"
            >
              <Mail size={16} aria-hidden="true" /> sindacale@confcommercioroma.it
            </a>
            <Link
              to="/contatti"
              className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity"
            >
              Contattaci <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-white/80">
            <Phone size={15} className="text-white/70" aria-hidden="true" />
            {TELEFONI.map((tel, i) => (
              <a
                key={tel}
                href={`tel:+39${tel.replace(/\./g, "")}`}
                className="hover:text-white transition-colors"
              >
                {tel}{i < TELEFONI.length - 1 ? " ·" : ""}
              </a>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
